import {
  BidAmountComponent,
  BondPriceComponent,
  CouponPaymentComponent,
  GSpreadComponent,
  MaturityDateComponent,
  MinimumInvestmentComponent,
  OfferAmountComponent,
  OutstandingAmountComponent,
  PaymentFrequencyComponent,
  YieldComponent,
} from "@goodfill/domain-properties-components";
import { Button, Card, CardHeader, CardRow, CardSection, Field, Flex, Span } from "@goodfill/ui-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { iconUrlByTicker } from "../../../assets/tickerAssetsIndex";
import { route } from "../../../routes";
import { PricedBond } from "../PricedBond";
import { BatchBuyRowCheckbox } from "../batchBuy/SelectedToBatchBuyContext";
import { FavorBond } from "../detail/FavorBond";
import "./styles/BondCardWide.scss";
import "../detail/FavorBond.scss";
import { TIMEZONE_NEY_YORK } from "../../../locale/timezone";

interface Props {
  item: PricedBond;
  style?: React.CSSProperties;
}

export const BondCardWide: React.FC<Props> = ({ item, style }) => {
  const [detailsButtonIsHighlighted, setDetailsButtonIsHighlighted] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onMouseEnterDetailsButton = () => {
    setDetailsButtonIsHighlighted(true);
  };
  const onMouseLeaveDetailsButton = () => {
    setDetailsButtonIsHighlighted(false);
  };

  const navigateToInvest = () => {
    navigate(route.orderBuy(item.bondIdCusip));
  };

  const issuerName = item.issuerName;
  const iconUrl = iconUrlByTicker(item.issuerTicker);

  return (
    <Card wide={true} className="relative">
      <CardHeader wide={true} className="py-5 card-header" style={style}>
        <>
          <Flex direction="row" gap={4} items="center">
            {iconUrl ? <img src={iconUrl} alt="" style={{ height: "45px", width: "45px" }} /> : null}
            <div className="text-base">{issuerName}</div>
          </Flex>

          <Flex direction="row" gap={2} items="center" className="favorBond_starResponsive">
            <FavorBond pricedBond={item} size={25} />
            <Flex items="center">
              <Button
                fill
                className="wide-card-buttons"
                id={`invest-${item.bondIdCusip}`}
                data-testid={`invest-${item.bondIdCusip}`}
                primaryGradient={true}
                uppercase={false}
                onClick={navigateToInvest}
              >
                {t("callToAction.invest")}
              </Button>
            </Flex>
            <Flex items="center">
              <Button
                fill
                className="wide-card-buttons"
                data-testid={`details-${item.bondIdCusip}`}
                uppercase={false}
                blue={true}
                primary={detailsButtonIsHighlighted}
                outlined={true}
                onMouseEnter={onMouseEnterDetailsButton}
                onMouseLeave={onMouseLeaveDetailsButton}
                onClick={() => navigate(`/bonds/${item.bondIdCusip}`)}
              >
                {t("callToAction.details")}
              </Button>
            </Flex>
            <BatchBuyRowCheckbox rowId={item.bondIdCusip} />
          </Flex>
        </>
      </CardHeader>
      <CardSection className="bg-white px-7 pt-7 pb-6">
        <Flex direction="row" gap={4} justify="between">
          <CardRow className="list-view hide-scrollbar">
            <Field textAlign="left" label={t("bond.ticker")}>
              {item.issuerTicker}
            </Field>
            <Field textAlign="center" label={t("bond.couponPayment")}>
              <CouponPaymentComponent value={item.couponPayment} />
            </Field>
            <Field textAlign="center" label={t("bond.maturityDate")}>
              <MaturityDateComponent value={item.maturityDate} timezone={TIMEZONE_NEY_YORK} />
            </Field>
            <Field label={t("bond.offerYield")} textAlign="center">
              <YieldComponent value={item.askYield} />
            </Field>
            <Field label={t("bond.offerPrice")} textAlign="center">
              <BondPriceComponent value={item.askPrice} />
            </Field>
            <Field label={t("bond.minimumPiece")} textAlign="center">
              <MinimumInvestmentComponent value={item.minimumPiece.amount} />
            </Field>
            <Field label={t("bond.outstandingAmount")} textAlign="center">
              <OutstandingAmountComponent value={item.outstandingAmount.amount} />
            </Field>
            <Field label={t("bond.ratings.S&P")} textAlign="center">
              {item.ratings.sp ?? ""}
            </Field>
            <Field label={t("bond.ratings.Moody")} textAlign="center">
              {item.ratings.moodys ?? ""}
            </Field>
          </CardRow>
        </Flex>
      </CardSection>

      <Flex
        className="flex-wrap xl:flex-nowrap gap-5-lg px-6 pb-5 BondCardWide_noPaddingOnSmall"
        direction="rowCol"
        gap={4}
        overflow="auto"
        justify="between"
      >
        <CardSection className="w-[26rem] bid-container-width-on-lg bid-container-width-on-sm">
          <CardRow justify="center" className="list-view pt-2.5">
            <Span font="bold" size="sm">
              {t("card.aboutBid")}
            </Span>
          </CardRow>
          <CardRow className="list-view  hide-scrollbar ">
            <Field textAlign="center" label={t("bond.bidYield")}>
              <YieldComponent value={item.bidYield} />
            </Field>
            <Field textAlign="center" label={t("bond.bidPrice")}>
              <BondPriceComponent value={item.bidPrice} />
            </Field>
            <Field textAlign="center" label={t("bond.gSpreadBid")}>
              <GSpreadComponent value={item.bidGSpread} />
            </Field>
            <Field textAlign="center" label={t("bond.bidSize")}>
              <BidAmountComponent value={item.bidAmount} />
            </Field>
          </CardRow>
        </CardSection>

        <CardSection className="w-[18rem] bid-container-width-on-lg bid-container-width-on-sm">
          <CardRow justify="center" className="list-view pt-2.5 hide-scrollbar">
            <Span font="bold" size="sm">
              {t("card.moreOnOffer")}
            </Span>
          </CardRow>
          <CardRow className="list-view  hide-scrollbar">
            <Field label={t("bond.gSpreadAsk")} textAlign="center">
              <GSpreadComponent value={item.askGSpread} />
            </Field>
            <Field label={t("bond.offerSize")} textAlign="center">
              <OfferAmountComponent value={item.bidAmount} />
            </Field>
          </CardRow>
        </CardSection>

        <CardSection className="w-[31rem] work-container-width-on-lg">
          <CardRow justify="center" className="list-view pt-2.5">
            <Span font="bold" size="sm">
              {t("card.theWorks")}
            </Span>
          </CardRow>
          <CardRow className="list-view">
            <Field label={t("bond.paymentRank")} textAlign="center">
              {item.paymentRank}
            </Field>
            <Field label={t("bond.industry")} textAlign="center">
              {item.industry}
            </Field>
            <Field label={t("bond.paymentFrequency")} textAlign="center">
              <PaymentFrequencyComponent value={item.couponFrequency} />
            </Field>
            <Field label={t("bond.cusip")} textAlign="center">
              {`${item.bondIdCusip}`}
            </Field>
          </CardRow>
        </CardSection>
      </Flex>
    </Card>
  );
};
